import { useEffect } from "react";
import { produce } from "immer";
import { CircleQuestionMarkIcon } from "lucide-react";

import { getText } from "shared/lib";
import {
  TreePropertyType,
  type TreeDescriptor,
  type TrunkTypeTreePropertyDescriptor,
  updateTreeProperty,
} from "./lib";

import { Button, Separator, TooltipProvider } from "shared/components";
import { TreeDialog2 } from "./treeDialog";
import { SizeEstimateField } from "./treeDialog/sizeEstimateField/SizeEstimateField";
import { DistanceField } from "./treeDialog/DistanceField";
import { IsNativeSpeciesField } from "./treeDialog/IsNativeSpeciesField";
import { GroundLevelAlterationField } from "./treeDialog/GroundLevelAlterationField";
import { TrunkTypeField } from "./treeDialog/TrunkTypeField";
import { MultiStemTreeDiagram } from "./treeDialog/multiStemTreeDiagram";
import { TreeDiagram } from "./treeDialog/treeDiagram";
import { DBHField } from "./treeDialog/DBHField";
import { CircumferenceField } from "./treeDialog/CircumferenceField";
import { MeasurementTypeField } from "@/components/treeModule/treeDialog/MeasurementTypeField";
import { TreeMeasurementsDialogContainer } from "./treeMeasurementsHelp";

export interface NearbyTreeDialogProps {
  onChangeTreeDescriptors?: (treeDescriptors: Array<TreeDescriptor>) => void;
  onClickSubmit?: (treeDescriptor: TreeDescriptor) => void;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  treeDescriptor: TreeDescriptor | undefined | null;
  treeDescriptors: Array<TreeDescriptor>;
}

export function NearbyTreeDialog({
  onChangeTreeDescriptors,
  onClickSubmit,
  onOpenChange,
  open,
  treeDescriptor,
  treeDescriptors,
}: NearbyTreeDialogProps) {
  const dbhPropertyExists = treeDescriptor?.properties.has(
    TreePropertyType.dbh,
  );

  const circumferencePropertyExists = treeDescriptor?.properties.has(
    TreePropertyType.circumference,
  );

  const treeMeasurementsType = treeDescriptor?.properties.get(
    TreePropertyType.measurement_type,
  );

  const trunkType = treeDescriptor?.properties.get(TreePropertyType.trunk_type);

  const dbh = treeDescriptor?.properties.get(TreePropertyType.dbh);

  const circumference = treeDescriptor?.properties.get(
    TreePropertyType.circumference,
  );

  const distance = treeDescriptor?.properties.get(TreePropertyType.distance);

  const handleChangeTreeDescriptor = (
    changedTreeDescriptor: TreeDescriptor,
  ) => {
    onChangeTreeDescriptors?.(
      produce(treeDescriptors, (draft) => {
        const index = draft.findIndex(
          (td) => td.treeId === changedTreeDescriptor?.treeId,
        );
        if (index > -1) {
          draft[index] = { ...changedTreeDescriptor };
        }
      }),
    );
  };

  useEffect(() => {
    if (treeDescriptor && treeMeasurementsType?.value === "estimate") {
      const newTreeDescriptor =
        updateTreeProperty<TrunkTypeTreePropertyDescriptor>(
          treeDescriptor,
          TreePropertyType.trunk_type,
          (draft) => {
            draft.value = "single" as TrunkTypeTreePropertyDescriptor["value"];
          },
        );
      handleChangeTreeDescriptor(newTreeDescriptor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeDescriptor?.treeId, treeMeasurementsType]);

  const renderTreeDiagram = () => {
    let diameter = 0.1;
    let label = "";
    if (dbh?.value) {
      diameter = parseFloat(dbh.value);
      label = `${diameter.toFixed(2)}m`;
    } else if (circumference?.value) {
      const c = parseFloat(circumference.value);
      diameter = (c / 3.5) * 1.5;
      label = `${c.toFixed(2)}m`;
    }

    return trunkType?.value === "multi" ? (
      <MultiStemTreeDiagram
        label={label}
        treeDiameter={diameter}
        treeDistance={parseInt(distance?.value || "1") - 1}
      />
    ) : (
      <TreeDiagram
        label={label}
        treeDiameter={diameter}
        treeDistance={parseInt(distance?.value || "1") - 1}
      />
    );
  };

  const renderTreeMeasurementsHelp = () => {
    return (
      <TreeMeasurementsDialogContainer treeDescriptor={treeDescriptor}>
        {(setOpen) => (
          <Button
            className="text-bodysm w-min gap-1"
            onClick={() => setOpen(true)}
            size="tight"
            variant="link"
          >
            <CircleQuestionMarkIcon className="size-4" />{" "}
            {getText("How to measure a tree")}
          </Button>
        )}
      </TreeMeasurementsDialogContainer>
    );
  };

  if (!treeDescriptor) return;

  return (
    <TooltipProvider>
      <TreeDialog2
        onClickSubmit={onClickSubmit}
        onOpenChange={onOpenChange}
        open={open}
        treeDescriptor={treeDescriptor}
      >
        <div>
          <h3 className="text-bodylg text-textPrimary">
            {getText("How large is this tree/vegetation?")}
          </h3>
          <p className="text-bodymd text-textSubdued">
            {getText(
              "Providing detailed measurements will help us give you a more accurate result.",
            )}
          </p>
        </div>

        <MeasurementTypeField
          onChange={handleChangeTreeDescriptor}
          treeDescriptor={treeDescriptor}
        />

        {treeMeasurementsType?.value === "estimate" && (
          <>
            <SizeEstimateField
              onChange={handleChangeTreeDescriptor}
              treeDescriptor={treeDescriptor}
            />

            <DistanceField
              label={getText(
                "How far away is this tree from your proposed works?",
              )}
              onChange={handleChangeTreeDescriptor}
              treeDescriptor={treeDescriptor}
            />

            <IsNativeSpeciesField
              onChange={handleChangeTreeDescriptor}
              treeDescriptor={treeDescriptor}
            />

            <GroundLevelAlterationField
              onChange={handleChangeTreeDescriptor}
              treeDescriptor={treeDescriptor}
            />
          </>
        )}

        {treeMeasurementsType?.value === "detailed" && (
          <>
            <TrunkTypeField
              onChange={handleChangeTreeDescriptor}
              treeDescriptor={treeDescriptor}
            />

            {renderTreeDiagram()}

            {dbhPropertyExists && (
              <>
                <DBHField
                  hideMeasurementEstimationAccordion
                  isMultiStem={trunkType?.value === "multi"}
                  onChange={handleChangeTreeDescriptor}
                  treeDescriptor={treeDescriptor}
                />

                {renderTreeMeasurementsHelp()}
              </>
            )}

            {circumferencePropertyExists && (
              <>
                <CircumferenceField
                  hideMeasurementEstimationAccordion
                  isMultiStem={trunkType?.value === "multi"}
                  onChange={handleChangeTreeDescriptor}
                  treeDescriptor={treeDescriptor}
                />

                {renderTreeMeasurementsHelp()}
              </>
            )}

            <Separator />

            <DistanceField
              label={getText(
                "How far away is this tree from your proposed works?",
              )}
              onChange={handleChangeTreeDescriptor}
              treeDescriptor={treeDescriptor}
            />

            <IsNativeSpeciesField
              onChange={handleChangeTreeDescriptor}
              treeDescriptor={treeDescriptor}
            />

            <GroundLevelAlterationField
              onChange={handleChangeTreeDescriptor}
              treeDescriptor={treeDescriptor}
            />
          </>
        )}
      </TreeDialog2>
    </TooltipProvider>
  );
}
